import { designVariables } from "@studio3t/design-variables";
import {
  Icon,
  IconSVG,
  MicroButton,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useRef } from "react";
import styled from "styled-components";
import {
  isBlockedKey,
  numberInputAttrs,
  pasteReplacement,
  type StepperMode,
  stepped,
} from "./stepperValue";

const INPUT = designVariables.ui.input;
const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

// Underline input box hosting the bare input, optional unit and the count
// control, so the border spans all of them (Figma TextInput 10651-14631 with
// Countcontrol 40001416-4333).
const StepperBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${INPUT.gapinternal}px;
  height: ${INPUT.height}px;
  padding: 0 ${INPUT.paddingw}px;
  border-bottom: 1px solid ${(props) => props.theme.inputDefaultBorder};
  cursor: text;

  &:hover {
    border-bottom-color: ${(props) => props.theme.inputHoverBorder};
  }

  &:focus-within {
    border-bottom-color: ${(props) => props.theme.inputActiveBorder};
  }
`;

// With a unit, the input shrink-wraps its digits so the unit sits right next
// to the number (Figma 14057-46641 renders "30 s"); tabular numerals make the
// ch measurement exact. Without a unit it just fills the box.
const BareInput = styled.input<{ $chars?: number }>`
  ${(props) =>
    props.$chars
      ? `width: calc(${props.$chars}ch + 2px);`
      : "flex: 1; min-width: 0;"}
  border: none;
  outline: none;
  background: transparent;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.default.regular.fontWeight};
  font-feature-settings: "lnum" 1, "tnum" 1;
  line-height: 1.2;
  color: ${(props) => props.theme.inputPristineText};
  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: ${(props) => props.theme.semanticTextPlaceholder};
  }
`;

// Absorbs the room between the unit and the count control so the unit stays
// glued to the number.
const Spacer = styled.span`
  flex: 1;
`;

const Unit = styled.span`
  flex-shrink: 0;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextBody};
`;

// The stacked up/down micro chevrons (Figma Countcontrol: 14×26).
const CountControl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FlippedChevron = styled.span`
  display: inline-flex;
  transform: rotate(180deg);
`;

interface StepperInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Unit suffix rendered inside the input box (e.g. "s"). */
  unit?: string;
  /** Floor for the count control (typed values are never rewritten).
   *  Defaults to 0, or to no floor at all in decimal mode. */
  min?: number;
  /** Accept decimals and a leading minus — the generator's field bounds.
   *  Integer fields (the default) filter keys and pastes to digits, so a
   *  typed value cannot go negative or fractional. */
  decimal?: boolean;
}

// type="number" still accepts exponent, sign and decimal keys — and input
// like "3-122-213123" then sits visibly in the field while the browser
// reports an empty value. Block what the mode disallows and clean pastes.
function sanitizePaste(
  e: React.ClipboardEvent<HTMLInputElement>,
  decimal: boolean,
  onChange: (value: string) => void,
) {
  const replacement = pasteReplacement(
    e.clipboardData.getData("text"),
    decimal,
  );
  if (replacement === null) return;
  e.preventDefault();
  if (replacement) onChange(replacement);
}

function blockDisallowedKeys(
  e: React.KeyboardEvent<HTMLInputElement>,
  decimal: boolean,
) {
  if (isBlockedKey(e.key, decimal)) e.preventDefault();
}

// The shrink-wrapped input leaves dead space in the box; clicking anywhere
// in it (except the stepper buttons) should still focus the field.
function focusUnlessButton(
  e: React.MouseEvent<HTMLDivElement>,
  input: HTMLInputElement | null,
) {
  if ((e.target as HTMLElement).closest("button")) return;
  input?.focus();
}

// The stacked up/down micro buttons; each steps the value by one in the
// field's mode.
function CountControlButtons({
  label,
  value,
  mode,
  onChange,
}: {
  label: string;
  value: string;
  mode: StepperMode;
  onChange: (value: string) => void;
}) {
  const chevron = (
    <IconSVG
      icon={Icon.BaseChevron}
      size={BUTTON.chevron}
      svgColorKey={SVGColorKey.semanticTextBody}
    />
  );
  return (
    <CountControl>
      <MicroButton
        ref={(el) => el?.setAttribute("type", "button")}
        aria-label={`Increase ${label}`}
        icon={<FlippedChevron>{chevron}</FlippedChevron>}
        onClick={() => onChange(stepped(value, 1, mode))}
      />
      <MicroButton
        ref={(el) => el?.setAttribute("type", "button")}
        aria-label={`Decrease ${label}`}
        icon={chevron}
        onClick={() => onChange(stepped(value, -1, mode))}
      />
    </CountControl>
  );
}

// Number field with the design-system count control instead of the native
// browser spinner. Arrow keys still step natively via type="number".
export function StepperInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  unit,
  decimal = false,
  min = decimal ? Number.NEGATIVE_INFINITY : 0,
}: StepperInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const mode = { decimal, min };
  const chars = unit ? Math.max((value || placeholder).length, 1) : undefined;

  return (
    <StepperBox onClick={(e) => focusUnlessButton(e, inputRef.current)}>
      <BareInput
        ref={inputRef}
        $chars={chars}
        id={id}
        type="number"
        {...numberInputAttrs(mode)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => blockDisallowedKeys(e, decimal)}
        onPaste={(e) => sanitizePaste(e, decimal, onChange)}
        placeholder={placeholder}
      />
      {unit && (
        <>
          <Unit>{unit}</Unit>
          <Spacer />
        </>
      )}
      <CountControlButtons
        label={label}
        value={value}
        mode={mode}
        onChange={onChange}
      />
    </StepperBox>
  );
}
